import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import Button from 'carbon-react/lib/components/button';
import { format } from 'date-fns';

import { getForecastByIdOnboardingUrl, Logger, QUERY_PARAMS, ROUTES } from '@futrli/shared-services';
import { toastError, UILoader, UISystemIcon } from '@futrli/ui-components';
import { SCENARIO_ONBOARDING_STEP } from '@futrli/predict/forecast/onboarding/const';
import { CreateNewForecastPayload } from '@futrli/predict/forecast/dashboard/types';
import { createNewForecast } from '@futrli/predict/core/api/forecast';

import * as styles from './onboarding.scss';
import { OnboardingPageTitle } from '../../../../predict/src/forecast/onboarding/components/onboarding-page-title';

export const ForecastingOnboardingStart = () => {
  const [selectedForecastType, setSelectedForecastType] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const intl = useIntl();
  const navigate = useNavigate();

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const workspaceId = searchParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = searchParams.get(QUERY_PARAMS.ORG_ID);
  const usedIntegration = searchParams.get(QUERY_PARAMS.INTEGRATION);

  const handleNextClick = async () => {
    if (!workspaceId || !organisationId) {
      return;
    }
    if (selectedForecastType === 0) {
      navigate(
        `${ROUTES.ONBOARDING_ROUTE}${ROUTES.ONBOARDING_INITIAL}${ROUTES.BUDGET_DATE_SELECT}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${QUERY_PARAMS.ORG_ID}=${organisationId}`
      );
    } else if (usedIntegration === 'excel') {
      try {
        const requestBody: CreateNewForecastPayload = {
          name: intl.formatMessage({ id: 'predict.my_first_forecast', defaultMessage: 'My first forecast' }),
          kind: 'scenario-from-scratch',
          categories_with_ai_enabled: null,
          forecast_from_date: format(new Date(), 'yyyy-MM-dd')
        };

        const createdForecast = await createNewForecast(workspaceId, organisationId, requestBody);

        navigate(
          `${getForecastByIdOnboardingUrl(workspaceId, organisationId, createdForecast.id, false, false)}/${
            SCENARIO_ONBOARDING_STEP.SELECT_TYPE
          }${ROUTES.SALES}?${QUERY_PARAMS.ONBOARDING_FORECAST_ID}=${createdForecast.id}`
        );
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    } else {
      navigate(
        `${ROUTES.ONBOARDING_ROUTE}${ROUTES.ONBOARDING_INITIAL}${ROUTES.SCENARIO_TYPE}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${QUERY_PARAMS.ORG_ID}=${organisationId}`
      );
    }
  };

  if (!workspaceId || !organisationId) {
    return null;
  }

  if (isLoading) {
    return <UILoader isFull />;
  }

  return (
    <div>
      <div className={styles.contentContainer}>
        <OnboardingPageTitle
          title={
            <FormattedMessage
              id="onboarding.what_type_of_forecast"
              defaultMessage="What type of budget or forecast do you want?"
            />
          }
          description={
            <FormattedMessage
              id="onboarding.different_option_later"
              defaultMessage="You can create the different option later."
            />
          }
        />
        <div style={{ display: 'flex', marginBottom: 20 }}>
          <div
            className={classNames(styles.onboardingStartButton, {
              [styles.onboardingStartButtonActive]: selectedForecastType === 0
            })}
            onClick={() => setSelectedForecastType(0)}
          >
            {selectedForecastType === 0 ? (
              <UISystemIcon iconName="double-check-onboarding" width={40} height={40} />
            ) : (
              <UISystemIcon iconName="onboarding-budget" width={40} height={40} />
            )}
            <FormattedMessage id="predict.one_year_p_l_only" defaultMessage="1 year P&L only" />
          </div>
          <div
            className={classNames(styles.onboardingStartButton, {
              [styles.onboardingStartButtonActive]: selectedForecastType === 1
            })}
            style={{ marginLeft: 40 }}
            onClick={() => setSelectedForecastType(1)}
          >
            {selectedForecastType === 1 ? (
              <UISystemIcon iconName="double-check-onboarding" width={40} height={40} />
            ) : (
              <UISystemIcon iconName="create-forecast-start" width={40} height={40} />
            )}
            <FormattedMessage id="onboarding.3_year_cash_flow_forecast" defaultMessage="3 year cash flow forecast" />
          </div>
        </div>
        <Button disabled={selectedForecastType === null} onClick={handleNextClick} size="large" buttonType="primary">
          <FormattedMessage id="common.next" defaultMessage="Next" />
        </Button>
      </div>
    </div>
  );
};
